import { Lightning } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { ButtonConstant } from './ButtonConstants';

const Modes = {
  primary: {
    normal: {
      border: true,
      borderColor: Theme.Colors.Button.Primary.Normal.Border,
      background: Theme.Colors.Button.Primary.Normal.Fill,
      foreground: Theme.Colors.Button.Primary.Normal.Text,
    },
    focus: {
      border: true,
      borderColor: Theme.Colors.Button.Primary.Focus.Border,
      background: Theme.Colors.Button.Primary.Focus.Fill,
      foreground: Theme.Colors.Button.Primary.Focus.Text,
    },
  },
  dialog: {
    normal: {
      background: Theme.Colors.Button.Dialog.Normal.background,
      foreground: Theme.Colors.Button.Dialog.Normal.foreground,
    },
    focus: {
      background: Theme.Colors.Button.Dialog.Focus.background,
      foreground: Theme.Colors.Button.Dialog.Focus.foreground,
    },
  },
  text: {
    normal: {
      background: Theme.Colors.Button.Primary.Normal.Fill,
      foreground: Theme.Colors.Button.Primary.Normal.Text,
    },
    focus: {
      background: Theme.Colors.Button.Primary.Focus.Fill,
      foreground: Theme.Colors.Button.Primary.Focus.Text,
    },
  },
  textButton: {
    normal: {
      background: Theme.Colors.Button.TextButton.Normal.Fill,
      foreground: Theme.Colors.Button.TextButton.Normal.Text,
    },
    focus: {
      border: true,
      borderColor: Theme.Colors.Button.TextButton.Focus.Border,
      background: Theme.Colors.Button.TextButton.Focus.Fill,
      foreground: Theme.Colors.Button.TextButton.Focus.Text,
    },
  },
  textButtonActivated: {
    normal: {
      background: Theme.Colors.Button.TextButton.Active.Fill,
      foreground: Theme.Colors.Button.TextButton.Active.Text,
    },
    focus: {
      border: true,
      borderColor: Theme.Colors.Button.TextButton.Focus.Border,
      background: Theme.Colors.Button.TextButton.Focus.Fill,
      foreground: Theme.Colors.Button.TextButton.Focus.Text,
    },
  },
};

export class Button extends Lightning.Component {
  static _template() {
    return {
      h: ButtonConstant.defaultH,
      flex: {
        direction: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: ButtonConstant.horizontalPadding,
        paddingRight: ButtonConstant.horizontalPadding,
      },
      Background: {
        flexItem: false,
        w: w => w,
        h: h => h,
        rect: true,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: ButtonConstant.cornerRadius,
        },
      },
      IconLeft: {
        texture: {
          type: Lightning.textures.ImageTexture,
        },
        visible: false,
      },
      IconLeftMargin: {
        flexItem: {
          marginRight: ButtonConstant.innerMargin,
        },
        visible: false,
      },
      Label: {
        text: {
          offsetY: 64,
          ...Theme.Fonts.Bold,
          wordWrap: false,
          verticalAlign: 'middle',
          textOverflow: 'ellipsis',
        },
      },
      IconRightMargin: {
        flexItem: {
          marginLeft: ButtonConstant.innerMargin,
        },
        visible: false,
      },
      IconRight: {
        texture: {
          type: Lightning.textures.ImageTexture,
        },
        visible: false,
      },
    };
  }

  _init() {
    this.buttonActive = false;
    this.isFocused = false;
  }

  get buttonMode() {
    return this._buttonMode;
  }

  set buttonMode(buttonMode) {
    this._buttonMode = buttonMode;
    this._updateButtonColors();
  }

  get buttonActive() {
    return this._buttonActive;
  }

  set buttonActive(active) {
    this._buttonActive = active;
    this._updateButtonColors();
  }

  get label() {
    const labelTag = this.tag('Label');
    const labelTexture = labelTag.text;
    return labelTexture.text;
  }

  set label(label) {
    const elem = this.tag('Label');
    const elemTexture = elem.text;
    elemTexture.text = label;

    if (label) {
      this.tag('IconLeftMargin').visible = !!this.iconLeft;
      this.tag('IconRightMargin').visible = !!this.iconRight;
    }
  }

  get iconLeft() {
    return this.tag('IconLeft').src;
  }

  set iconLeft(src) {
    this._setIcon(this.tag('IconLeft'), src);
    this.tag('IconLeftMargin').visible = src != null && this.label;
  }

  get iconRight() {
    return this.tag('IconRight').src;
  }

  set iconRight(src) {
    this._setIcon(this.tag('IconRight'), src);
    this.tag('IconRightMargin').visible = src != null && this.label;
  }

  _setIcon(icon, src) {
    if (src) {
      icon.src = src;
      icon.visible = true;
      icon.patch({
        texture: {
          src: src,
          resizeMode: {
            type: 'contain',
            h: ButtonConstant.iconSize,
          },
        },
      });
    } else {
      icon.visible = false;
    }
  }

  _getButtonModeString() {
    if (this.buttonActive) {
      return this.buttonMode + 'Activated';
    } else if (this.buttonMode in Modes) {
      return this.buttonMode;
    } else {
      // Reasonable default
      return 'primary';
    }
  }

  _updateButtonColors() {
    const mode = this._getButtonModeString();
    const state = this.isFocused ? 'focus' : 'normal';

    let currState = Modes[mode][state];
    this.tag('Background').shader.stroke = currState.border ? ButtonConstant.stroke : 0;
    if (currState.border) this.tag('Background').shader.strokeColor = currState.borderColor;
    this.tag('Background').shader.fillColor = currState.background;

    for (const fgElem of ['IconLeft', 'Label', 'IconRight']) {
      this.tag(fgElem).color = currState.foreground;
    }
  }

  _focus() {
    this.isFocused = true;
    this._updateButtonColors();
  }

  _unfocus() {
    this.isFocused = false;
    this._updateButtonColors();
  }

  _handleEnter() {
    this.signal('onActivate', this);
  }
}
